'use client';
import styled from '@mui/system/styled';
import { useCallback, useEffect, useRef, useState } from 'react';
import Draggable from 'react-draggable';
import { MdDelete, MdEdit } from 'react-icons/md';
import { Resizable } from 'react-resizable';
import Xarrow, { useXarrow, Xwrapper } from 'react-xarrows';
import { connectField } from 'uniforms';

import ColorPickerInput from '../../components/ColorPickerInput';
import { IconModal } from '../../components/IconModal';

import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import { CircularProgress } from '@mui/material';
import Button from '@mui/material/Button';
import Modal from '@mui/material/Modal';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { usePathname } from 'next/navigation';
import { useIcon } from '../../Hooks/useIcon';

const RootDiv = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  '& button': {
    margin: '0 2px',
  },
});

const IconContainer = styled('div')({
  margin: 5,
  fontSize: 30,
  display: 'flex',
  '& svg': {
    fontSize: 36, // This ensures svg elements within .icon-container get their font size styling
  },
});

const StyledInput = styled('input')({
  padding: '15px 8px',
  margin: '0 2px',
});

const StyledLabel = styled('label')({
  margin: '0 5px',
  fontWeight: 700,
});

const DragContainerOuter = styled('div')({
  maxWidth: 1200,
  minWidth: 1200,
  maxHeight: 620,
  overflow: 'auto',
  margin: '0 auto',
});

const IconFieldActions = styled('div')({
  margin: '0 auto',
  padding: '15px 0',
});

const PaperDiv = styled('div')(({ theme }: any) => ({
  position: 'absolute',
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[5],
  padding: '5px',
  borderRadius: '4px',
  outline: 'none',
  width: '99%',
  height: '99%',
  overflow: 'auto',
  insetArea: 'center',
}));

const PaperInnerDiv = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  '& > *': {
    margin: '7px',
  },
});

const HeaderDiv = styled('div')({
  margin: '10px 0',
  textAlign: 'center',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

interface DraggableBoxProps {
  id: string;
  position: { x: number; y: number };
  bgColor: string;
  textColor: string;
  iconColor: string;
  borderColor: string;
  hideIcon: boolean;
  smallSize: boolean;
  showList: boolean;
  showTitle: boolean;
  listItems: string[];
  defaultText: string;
  icon: string;
  updatePosOnStop: (
    id: string,
    position: { x: number; y: number },
    type: string
  ) => void;
  handleClick: () => void;
  showBlockEdit: () => void;
}

const DraggableBox = ({
  id,
  position,
  bgColor,
  textColor,
  iconColor,
  borderColor,
  hideIcon,
  showList,
  showTitle,
  listItems,
  defaultText,
  icon,
  updatePosOnStop,
  handleClick,
  showBlockEdit,
  onDelete,
}: any): any => {
  const updateXarrow = useXarrow();

  const onControlledDragStop = (e: any, data: any) => {
    updateXarrow();
    updatePosOnStop(id, { x: data.x, y: data.y }, 'block');
  };

  return (
    <Draggable
      grid={[25, 25]}
      cancel="strong"
      bounds={'#dragContainerOuterModal'}
      onDrag={updateXarrow}
      onStop={onControlledDragStop}
      position={{ x: position.x, y: position.y }}
    >
      <div
        id={id}
        onClick={handleClick}
        style={{
          opacity: 1,
          position: 'absolute',
          zIndex: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          fontWeight: 700,
          backgroundColor: bgColor,
          color: textColor,
          border: `3px solid ${borderColor}`,
          borderRadius: '12px',
          cursor: 'pointer',
          fontSize: 18,
          padding: '2px 5px',
        }}
        className="box"
      >
        <div className="flex ai-center">
          {!hideIcon && (
            <IconContainer className="flex ai-center">
              <TitleIcon color={iconColor} iconName={icon} />
            </IconContainer>
          )}
          {showTitle && <div className="d-box-title">{defaultText || id}</div>}
          <strong onClick={showBlockEdit} style={{ cursor: 'pointer' }}>
            <MdEdit />
          </strong>
          <strong
            title="Delete element"
            style={{ cursor: 'pointer', marginLeft: 4, display: 'inline-flex' }}
            onClick={(e) => {
              e.stopPropagation();
              onDelete?.();
            }}
          >
            <MdDelete color="#c0392b" />
          </strong>
        </div>

        {listItems?.length && showList ? (
          <div className="flex-col">
            {listItems.map((item: any, idx: any) => (
              <span key={idx} style={{ fontSize: 14 }}>
                {item}
              </span>
            ))}
          </div>
        ) : null}
      </div>
    </Draggable>
  );
};

const DraggableDot = ({
  id,
  position,
  color,
  updatePosOnStop,
  handleClick,
  onDelete,
}: any): any => {
  const updateXarrow = useXarrow();

  const onControlledDragStop = (e: any, data: any) => {
    updateXarrow();
    updatePosOnStop(id, { x: data.x, y: data.y }, 'dot');
  };

  return (
    <Draggable
      cancel="strong"
      bounds={'#dragContainerOuterModal'}
      onDrag={updateXarrow}
      onStop={onControlledDragStop}
      position={{ x: position.x, y: position.y }}
    >
      <div
        id={id}
        onClick={handleClick}
        style={{
          opacity: 1,
          position: 'absolute',
          zIndex: 2,
          width: 30,
          height: 30,
          backgroundColor: color || '#222',
          borderRadius: '50%',
          cursor: 'pointer',
          padding: 5,
        }}
        className="box"
      >
        <strong
          title="Delete anchor"
          style={{
            position: 'absolute',
            top: -10,
            right: -10,
            cursor: 'pointer',
            background: '#fff',
            borderRadius: '50%',
            display: 'inline-flex',
            lineHeight: 0,
          }}
          onClick={(e) => {
            e.stopPropagation();
            onDelete?.();
          }}
        >
          <MdDelete size={16} color="#c0392b" />
        </strong>
      </div>
    </Draggable>
  );
};

const TitleIcon = ({
  color,
  iconName,
}: {
  color: string;
  iconName: string;
}) => {
  const Icon = useIcon({ color, iconName });
  return Icon;
};

// Build an id that doesn't collide with existing elements. Using items.length+1
// alone can collide after a middle element is deleted (e.g. delete Block2 from
// [Block1, Block2, Block3] then create -> Block3 again).
function getUniqueId(prefix: string, items: any[]) {
  const existing = new Set(items.map((i: any) => i.id));
  let n = items.length + 1;
  while (existing.has(prefix + n)) n++;
  return prefix + n;
}

interface DraggablesModalProps {
  value: string;
  onChange: (value: string) => void;
  label: string;
}

export const DraggablesModal = connectField(
  ({ value, onChange, label, uploadImage }: any): any => {
    const [ShowDraggableModal, setShowDraggableModal] = useState(false);
    const [Blocks, setBlocks] = useState<any>([]);
    const [Lines, setLines] = useState<any>([]);
    const [Dots, setDots] = useState<any>([]);
    const [LineType, setLineType] = useState('straight');
    const [showLoading, setShowLoading] = useState(false);
    const [ShowConnectionEdit, setShowConnectionEdit] = useState(false);
    const [ShowBlockEdit, setShowBlockEdit] = useState(false);
    const [EditingBlock, setEditingBlock] = useState<any>(null);
    const [FromBlockId, setFromBlockId] = useState(null);
    const [ToBlockId, setToBlockId] = useState(null);
    const [ImageUrl, setImageUrl] = useState('');
    const [Width, setWidth] = useState(1200);
    const [Height, setHeight] = useState(800);
    const [GridSnap, setGridSnap] = useState<any>(false);
    const [AnchorsLinesColor, setAnchorsLinesColor] = useState('#222');
    const [ContainerHeight, setContainerHeight] = useState(300);
    // Chosen display width for the image. When set, the image is rendered
    // left-anchored at this width in both the editor and the viewer so hotspot
    // pixel coordinates share one coordinate space. null => legacy (centered,
    // natural size) rendering, kept for backward compatibility.
    const [DisplayWidth, setDisplayWidth] = useState<number | null>(null);
    // Horizontal placement of the whole element (image + hotspots) on the screen.
    const [Align, setAlign] = useState<'left' | 'center' | 'right'>('center');
    const [NaturalSize, setNaturalSize] = useState<{ w: number; h: number } | null>(
      null
    );
    const [ImgBaseWidth, setImgBaseWidth] = useState<number | null>(null);
    const resizeBaseRef = useRef<{ oldW: number }>({ oldW: 0 });
    // True right after a fresh upload, so we left-anchor the new image on load
    // instead of switching coordinate systems on the first resize (which made
    // the image visibly jump to the top-left).
    const justUploadedRef = useRef(false);
    const path = usePathname();

    const results: { tenantId: string; companyId: string; screenId: string } =
      parseUrl(path)!;
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (value) {
        const jsonValue = JSON.parse(value);
        setBlocks(jsonValue.Blocks);
        setLines(jsonValue.Lines);
        setDots(jsonValue.Dots);
        setImageUrl(jsonValue.imageUrl);
        setWidth(jsonValue.Width);
        setHeight(jsonValue.Height);
        setAnchorsLinesColor(jsonValue.AnchorsLinesColor);
        setContainerHeight(
          jsonValue.ImageUrl ? 'auto' : jsonValue.ContainerHeight
        );
        setDisplayWidth(jsonValue.DisplayWidth ?? null);
        setAlign(jsonValue.Align ?? 'center');
      }
    }, [value]);

    // useEffect(() => {
    //   if (FromBlockId && ToBlockId) {
    //     createConnection(FromBlockId, ToBlockId);
    //   }
    // }, [FromBlockId, ToBlockId]);

    function createBlock() {
      const boxId = getUniqueId('Block', Blocks);
      const box = {
        id: boxId,
        position: { x: 0, y: 0 },
        defaultText: '',
        icon: 'Anchor 2',
        docLink: '',
        bgColor: '#fff',
        textColor: '#222',
        iconColor: '#222',
        borderColor: '#fff',
        hideIcon: false,
        smallSize: false,
        visible: true,
        listItems: [],
        showList: false,
        showTitle: true,
      };
      setBlocks([...Blocks, box]);
    }

    function addListItem() {
      const blocks = [...Blocks];
      let listItems = blocks[EditingBlock!].listItems || [];
      listItems.push('Enter Text');
      blocks[EditingBlock!].listItems = listItems;
      setBlocks(blocks);
    }

    function removeListItem(index: number) {
      const blocks = [...Blocks];
      blocks[EditingBlock!].listItems.splice(index, 1);
      setBlocks(blocks);
    }

    function handleBlockItemsChange(e: any, index: number) {
      const blocks = [...Blocks];
      blocks[EditingBlock!].listItems[index] = e.target.value;
      setBlocks(blocks);
    }

    function createDot() {
      const dotId = getUniqueId('Dot', Dots);
      const dot = {
        id: dotId,
        position: { x: 0, y: 0 },
        visible: true,
      };
      setDots([...Dots, dot]);
    }

    function handleTextBlockChange(e: any) {
      const blocks = [...Blocks];
      if (EditingBlock !== null) {
        blocks[EditingBlock].defaultText = e.target.value;
        setBlocks(blocks);
      }
    }

    function handleDocLinkChange(e: any) {
      const blocks = [...Blocks];
      if (EditingBlock !== null) {
        blocks[EditingBlock].docLink = e.target.value;
        setBlocks(blocks);
      }
    }

    function updatePosOnStop(
      id: string,
      position: { x: number; y: number },
      type: string
    ) {
      if (type === 'block') {
        const blocks = [...Blocks];
        const elemToUpdate = blocks.find((b) => b.id === id);
        if (elemToUpdate) {
          elemToUpdate.position = position;
          setBlocks(blocks);
        }
      } else {
        const dots = [...Dots];
        const elemToUpdate = dots.find((b) => b.id === id);
        if (elemToUpdate) {
          elemToUpdate.position = position;
          setDots(dots);
        }
      }
    }

    function onModalImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
      const img = e.currentTarget;
      if (img.naturalWidth && img.naturalHeight) {
        setNaturalSize({ w: img.naturalWidth, h: img.naturalHeight });
      }
      // Baseline width used before the author has picked a DisplayWidth. Uses the
      // rendered (maxWidth:100%-capped) width so the drag handle stays on-screen.
      if (ImgBaseWidth == null) {
        setImgBaseWidth(img.clientWidth || img.naturalWidth || null);
      }
      // For a freshly uploaded image, adopt the left-anchored layout immediately
      // so the first resize doesn't switch coordinate systems and jump. Legacy
      // saved screens (loaded from value) keep their existing centered layout.
      if (justUploadedRef.current) {
        justUploadedRef.current = false;
        if (DisplayWidth == null) {
          setDisplayWidth(img.clientWidth || img.naturalWidth || 0);
        }
      }
    }

    // On the first resize we switch from the legacy centered layout to the
    // left-anchored one. Re-base hotspot coordinates from container-origin to
    // image-origin so they stay glued to the same visual point.
    function onModalImageResizeStart() {
      if (DisplayWidth != null) {
        resizeBaseRef.current = { oldW: DisplayWidth };
        return;
      }
      const imgEl = document.getElementById('dragablesModalImg');
      const bx = imgEl?.offsetLeft ?? 0;
      const by = imgEl?.offsetTop ?? 0;
      const base =
        imgEl?.clientWidth || ImgBaseWidth || NaturalSize?.w || 0;
      if (bx || by) {
        setBlocks((prev: any) =>
          prev.map((b: any) => ({
            ...b,
            position: { x: b.position.x - bx, y: b.position.y - by },
          }))
        );
        setDots((prev: any) =>
          prev.map((d: any) => ({
            ...d,
            position: { x: d.position.x - bx, y: d.position.y - by },
          }))
        );
      }
      setDisplayWidth(base);
      resizeBaseRef.current = { oldW: base };
    }

    function onModalImageResize(_e: any, { size }: any) {
      setDisplayWidth(Math.round(size.width));
    }

    function onModalImageResizeStop(_e: any, { size }: any) {
      const newW = Math.round(size.width);
      const oldW = resizeBaseRef.current.oldW || newW;
      const k = oldW ? newW / oldW : 1;
      if (k !== 1) {
        setBlocks((prev: any) =>
          prev.map((b: any) => ({
            ...b,
            position: { x: b.position.x * k, y: b.position.y * k },
          }))
        );
        setDots((prev: any) =>
          prev.map((d: any) => ({
            ...d,
            position: { x: d.position.x * k, y: d.position.y * k },
          }))
        );
      }
      setDisplayWidth(newW);
    }

    const handleClick = () => {
      setShowDraggableModal(!ShowDraggableModal);
    };

    function handleLineTypeChange(e: any) {
      const lines = [...Lines];
      setLineType(e.target.value);
      lines[lines.length - 1].path = e.target.value;
      setLines(lines);
    }

    function createConnection(fromBlockId: string, toBlockId: string) {
      const lines = [...Lines];
      lines.push({ startElem: fromBlockId, endElem: toBlockId });
      setLines(lines);
    }

    function cancelConnection() {
      removeLastConnection();
      setShowConnectionEdit(false);
      setFromBlockId(null);
      setToBlockId(null);
    }

    function removeLastConnection() {
      const lines = [...Lines];
      lines.pop();
      setLines(lines);
    }

    function handleElemClick(block: any) {
      if (ShowConnectionEdit) {
        if (!FromBlockId) {
          setFromBlockId(block.id);
          !!ToBlockId && createConnection(block.id, ToBlockId);
        } else {
          if (block.id !== FromBlockId) {
            if (Lines.length) {
              const connectionExists = Lines.find(
                (l: any) =>
                  l.startElem === FromBlockId && l.endElem === block.id
              );
              if (connectionExists) {
                return;
              }
            }
            setToBlockId(block.id);
            !!FromBlockId && createConnection(FromBlockId, block.id);
          }
        }
      }
    }

    // Remove a block or dot by id, along with any lines connected to it, and
    // close the block edit panel (indexes shift after removal).
    function deleteElement(id: string) {
      setBlocks((prev: any) => prev.filter((b: any) => b.id !== id));
      setDots((prev: any) => prev.filter((d: any) => d.id !== id));
      setLines((prev: any) =>
        prev.filter((l: any) => l.startElem !== id && l.endElem !== id)
      );
      setShowBlockEdit(false);
      setEditingBlock(null);
    }

    function handleSubmit() {
      let containerWidth = '100%';
      if (ImageUrl) {
        const imgElem = document.getElementById('dragablesModalImg');
        containerWidth = imgElem?.clientWidth + 'px';
      }

      const jsonElements = {
        imageUrl: ImageUrl,
        Blocks: Blocks,
        Lines: Lines,
        Dots: Dots,
        Width: Width,
        Height: Height,
        AnchorsLinesColor: AnchorsLinesColor,
        ContainerHeight: ImageUrl ? 'auto' : ContainerHeight,
        ContainerWidth: containerWidth,
        DisplayWidth: DisplayWidth,
        Align: Align,
      };
      onChange(JSON.stringify(jsonElements));
      setShowDraggableModal(false);
    }
    function handleSelectedIcon(icon: any) {
      const blocks = JSON.parse(JSON.stringify(Blocks));
      blocks[EditingBlock].icon = icon;
      setBlocks(blocks);
    }

    function handleSelectedBlockProperty(name: any, value: any) {
      const blocks = [...Blocks];
      blocks[EditingBlock][name] = value;
      setBlocks(blocks);
    }

    const handleImageChange = useCallback(
      async (e: any) => {
        e.preventDefault();
        const file = e.target.files[0];
        setShowLoading(true);
        if (file && results) {
          const data = await uploadImage(file);
          if (data) {
            justUploadedRef.current = true;
            setImageUrl(data.url);
          }
          setShowLoading(false);
        }
      },
      [results?.tenantId, results?.companyId] // dependencies
    );

    return (
      <RootDiv style={{ marginTop: 10 }}>
        <StyledLabel htmlFor="textAlignSelect">{label}</StyledLabel>
        <div style={{ marginLeft: 5, paddingTop: 5 }}>
          <Button
            onClick={handleClick}
            style={{ marginBottom: 5 }}
            variant="contained"
          >
            Start
          </Button>

          {ShowDraggableModal && (
            <Modal
              aria-labelledby="draggables-modal"
              aria-describedby="draggables-modal"
              open={true}
            >
              <PaperDiv tabIndex={-1}>
                <PaperInnerDiv>
                  <h2 className="text-center">Draggables Editor</h2>
                  {!ShowConnectionEdit && (
                    <HeaderDiv>
                      <Button variant="contained" onClick={createBlock}>
                        Add Block
                      </Button>
                      <Button variant="contained" onClick={createDot}>
                        Add Anchor
                      </Button>
                      <Button
                        variant="contained"
                        disabled={Dots.length + Blocks.length < 2}
                        onClick={() => setShowConnectionEdit(true)}
                      >
                        Link Elements
                      </Button>

                      {Lines.length > 0 && (
                        <Button
                          variant="contained"
                          onClick={removeLastConnection}
                        >
                          Remove Last Link
                        </Button>
                      )}
                      <Button
                        variant="contained"
                        onClick={() => {
                          setLines([]);
                          setBlocks([]);
                          setDots([]);
                          setShowConnectionEdit(false);
                          setShowBlockEdit(false);
                        }}
                      >
                        Clear All
                      </Button>
                    </HeaderDiv>
                  )}
                  <div
                    className="flex ai-center"
                    style={{ justifyContent: 'center' }}
                  >
                    <StyledLabel htmlFor="gridSnap">
                      Snap Grid Movement Blocks:
                    </StyledLabel>
                    <StyledInput
                      type="checkbox"
                      onChange={() => setGridSnap(!GridSnap)}
                      value={GridSnap ? 'true' : 'false'}
                      name="gridSnap"
                      id="gridSnap"
                    />
                  </div>
                  <div
                    className="flex ai-center"
                    style={{ justifyContent: 'center' }}
                  >
                    {ShowConnectionEdit ? (
                      <>
                        {!FromBlockId ? (
                          <p
                            style={{
                              fontWeight: 700,
                              fontSize: 18,
                              margin: 0,
                              color: '#689f38',
                            }}
                          >
                            Click Origin Block
                          </p>
                        ) : FromBlockId && !ToBlockId ? (
                          <p
                            style={{
                              fontWeight: 700,
                              fontSize: 18,
                              margin: 0,
                              color: '#689f38',
                            }}
                          >
                            Click Destination Block
                          </p>
                        ) : null}
                        {FromBlockId && ToBlockId && (
                          <div className="flex ai-center">
                            <div style={{ marginLeft: 20, marginRight: 10 }}>
                              <StyledLabel htmlFor="lineType">
                                Line Type:{' '}
                              </StyledLabel>
                              <select
                                name="lineType"
                                id="lineType"
                                value={LineType}
                                onChange={handleLineTypeChange}
                              >
                                <option value="straight">straight</option>
                                <option value="smooth">smooth</option>
                                <option value="grid">grid</option>
                              </select>
                            </div>
                            <Button
                              variant="contained"
                              color="primary"
                              style={{ marginLeft: 10 }}
                              onClick={() => {
                                setShowConnectionEdit(false);
                                setFromBlockId(null);
                                setToBlockId(null);
                              }}
                            >
                              Done
                            </Button>
                            <Button
                              variant="contained"
                              onClick={cancelConnection}
                            >
                              Cancel
                            </Button>
                          </div>
                        )}
                      </>
                    ) : ShowBlockEdit ? null : (
                      <div className="flex ai-center">
                        {!ImageUrl && (
                          <>
                            <StyledLabel>
                              Container Height pixels (no image only):
                            </StyledLabel>
                            <TextField
                              variant="outlined"
                              size="small"
                              type="number"
                              InputProps={{
                                inputProps: { min: 100, max: 600 },
                              }}
                              value={ContainerHeight}
                              onChange={(e: any) =>
                                setContainerHeight(parseInt(e.target.value))
                              }
                            />
                          </>
                        )}

                        <ColorPickerInput
                          label="Anchors / Lines Color:"
                          setColor={setAnchorsLinesColor}
                          value={AnchorsLinesColor}
                        />

                        <Button
                          disabled={showLoading}
                          variant="contained"
                          component="label"
                          style={{ marginLeft: 10 }}
                        >
                          Upload Image
                          <StyledInput
                            name="bgImage"
                            type="file"
                            // Reset the value on open so re-selecting the SAME
                            // file (e.g. after Remove Image) still fires onChange.
                            onClick={(e: any) => {
                              e.currentTarget.value = '';
                            }}
                            onChange={handleImageChange}
                            hidden
                          />
                        </Button>
                        {ImageUrl && (
                          <Button
                            disabled={showLoading}
                            variant="outlined"
                            color="error"
                            style={{ marginLeft: 10 }}
                            startIcon={<MdDelete />}
                            onClick={() => {
                              setImageUrl('');
                              setDisplayWidth(null);
                              setNaturalSize(null);
                              setImgBaseWidth(null);
                              setContainerHeight((prev: any) =>
                                typeof prev === 'number' ? prev : 300
                              );
                            }}
                          >
                            Remove Image
                          </Button>
                        )}

                        <div
                          className="flex ai-center"
                          style={{ marginLeft: 16, gap: 6 }}
                        >
                          <StyledLabel>Align on screen:</StyledLabel>
                          <ToggleButtonGroup
                            size="small"
                            exclusive
                            value={Align}
                            onChange={(_e, v) => v && setAlign(v)}
                          >
                            <ToggleButton value="left" aria-label="align left">
                              <FormatAlignLeftIcon fontSize="small" />
                            </ToggleButton>
                            <ToggleButton
                              value="center"
                              aria-label="align center"
                            >
                              <FormatAlignCenterIcon fontSize="small" />
                            </ToggleButton>
                            <ToggleButton value="right" aria-label="align right">
                              <FormatAlignRightIcon fontSize="small" />
                            </ToggleButton>
                          </ToggleButtonGroup>
                        </div>
                      </div>
                    )}
                    {ShowBlockEdit && (
                      <div
                        className="flex-col"
                        style={{
                          padding: 10,
                          borderRadius: 8,
                          border: '1px solid #ccc',
                        }}
                      >
                        <div className="flex ai-center">
                          <StyledLabel>Enter text:</StyledLabel>
                          <TextField
                            variant="outlined"
                            size="small"
                            type="text"
                            value={Blocks[EditingBlock!].defaultText}
                            onChange={handleTextBlockChange}
                          />
                          <StyledLabel htmlFor="imgUrl">
                            Document Link:
                          </StyledLabel>
                          <TextField
                            variant="outlined"
                            style={{ width: 200 }}
                            size="small"
                            type="text"
                            defaultValue={Blocks[EditingBlock!].docLink || ''}
                            onChange={handleDocLinkChange}
                          />
                          <IconModal handleSelectedIcon={handleSelectedIcon} />
                        </div>
                        <div className="flex ai-center jcsb">
                          <ColorPickerInput
                            label="Background Color:"
                            setColor={(color: any) =>
                              handleSelectedBlockProperty('bgColor', color)
                            }
                            value={Blocks[EditingBlock!].bgColor}
                          />
                          <ColorPickerInput
                            label="Text Color:"
                            setColor={(color: any) =>
                              handleSelectedBlockProperty('textColor', color)
                            }
                            value={Blocks[EditingBlock!].textColor}
                          />
                          <ColorPickerInput
                            label="Icon Color:"
                            setColor={(color: any) =>
                              handleSelectedBlockProperty('iconColor', color)
                            }
                            value={Blocks[EditingBlock!].iconColor}
                          />
                          <ColorPickerInput
                            label="Border Color:"
                            setColor={(color: any) =>
                              handleSelectedBlockProperty('borderColor', color)
                            }
                            value={Blocks[EditingBlock!].borderColor}
                          />
                        </div>
                        <div
                          style={{ margin: '10px 0' }}
                          className="flex ai-center jcsb"
                        >
                          <div className="flex ai-center">
                            <StyledLabel htmlFor="gridSnap">
                              Hide Icon:
                            </StyledLabel>
                            <StyledInput
                              type="checkbox"
                              onChange={() =>
                                handleSelectedBlockProperty(
                                  'hideIcon',
                                  !Blocks[EditingBlock!].hideIcon
                                )
                              }
                              value={Blocks[EditingBlock!].hideIcon}
                              name="hideIcon"
                              id="hideIcon"
                            />
                          </div>

                          <div className="flex ai-center">
                            <StyledLabel htmlFor="showTitle">
                              Hide Title:
                            </StyledLabel>
                            <StyledInput
                              type="checkbox"
                              onChange={() =>
                                handleSelectedBlockProperty(
                                  'showTitle',
                                  !Blocks[EditingBlock!].showTitle
                                )
                              }
                              value={Blocks[EditingBlock!].showTitle}
                              name="showTitle"
                              id="showTitle"
                            />
                          </div>
                          <div className="flex ai-center">
                            <StyledLabel htmlFor="showList">
                              Show List:
                            </StyledLabel>
                            <StyledInput
                              type="checkbox"
                              onChange={() =>
                                handleSelectedBlockProperty(
                                  'showList',
                                  !Blocks[EditingBlock!].showList
                                )
                              }
                              value={Blocks[EditingBlock!].showList}
                              name="showList"
                              id="showList"
                            />
                          </div>
                          <Button variant="contained" onClick={addListItem}>
                            Add Item
                          </Button>
                        </div>
                        <div className="flex ai-center">
                          <div>
                            {Blocks[EditingBlock!].listItems.map(
                              (item: any, index: any) => (
                                <div key={index} className="flex ai-center">
                                  <StyledInput
                                    style={{ padding: 8 }}
                                    type="text"
                                    value={item}
                                    onChange={(e) =>
                                      handleBlockItemsChange(e, index)
                                    }
                                  />
                                  <MdDelete
                                    size={22}
                                    style={{ marginLeft: 5 }}
                                    onClick={() => removeListItem(index)}
                                  />
                                </div>
                              )
                            )}
                          </div>
                        </div>
                        <div className="flex ai-center" style={{ gap: 8 }}>
                          <Button
                            disabled={showLoading}
                            style={{ marginTop: 5 }}
                            color="primary"
                            variant="contained"
                            onClick={() => setShowBlockEdit(false)}
                          >
                            Done
                          </Button>
                          <Button
                            disabled={showLoading}
                            style={{ marginTop: 5 }}
                            color="error"
                            variant="outlined"
                            startIcon={<MdDelete />}
                            onClick={() =>
                              deleteElement(Blocks[EditingBlock!].id)
                            }
                          >
                            Delete element
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  <DragContainerOuter
                    id="dragContainerOuterModal"
                    ref={containerRef}
                    style={{
                      position: 'relative',
                      height: ImageUrl ? 'auto' : ContainerHeight + 'px',
                      width: ImageUrl ? 'auto' : '100%',
                    }}
                  >
                    <Xwrapper>
                      {Blocks.map((block: any, index: any) => (
                        <DraggableBox
                          grid={GridSnap ? [25, 25] : [1, 1]}
                          bgColor={block.bgColor}
                          textColor={block.textColor}
                          iconColor={block.iconColor}
                          borderColor={block.borderColor}
                          hideIcon={block.hideIcon}
                          smallSize={block.smallSize}
                          updatePosOnStop={updatePosOnStop}
                          position={block.position}
                          showList={block.showList}
                          showTitle={block.showTitle}
                          listItems={block.listItems}
                          key={block.id}
                          id={block.id}
                          icon={block.icon}
                          defaultText={block.defaultText}
                          handleClick={() => handleElemClick(block)}
                          showBlockEdit={() => {
                            if (!ShowConnectionEdit) {
                              setShowBlockEdit(true);
                              setEditingBlock(index);
                            }
                          }}
                          onDelete={() => deleteElement(block.id)}
                          visible={block.visible}
                        />
                      ))}
                      {Dots.map((block: any) => (
                        <DraggableDot
                          updatePosOnStop={updatePosOnStop}
                          color={AnchorsLinesColor}
                          position={block.position}
                          key={block.id}
                          id={block.id}
                          handleClick={() => handleElemClick(block)}
                          onDelete={() => deleteElement(block.id)}
                          visible={block.visible}
                        />
                      ))}
                      <div
                        className="lines"
                        style={{ position: 'relative', zIndex: 2 }}
                      >
                        {Lines.map((line: any, index: any) => (
                          <Xarrow
                            key={index}
                            showHead={false}
                            path={line.path || 'straight'}
                            color={AnchorsLinesColor || '#222'}
                            start={line.startElem}
                            end={line.endElem}
                          />
                        ))}
                      </div>
                    </Xwrapper>

                    {showLoading ? (
                      <CircularProgress />
                    ) : (() => {
                      if (!ImageUrl) return null;
                      const useDW = DisplayWidth != null;
                      const aspect = NaturalSize
                        ? NaturalSize.w / NaturalSize.h
                        : 1;
                      const curW =
                        DisplayWidth ?? ImgBaseWidth ?? NaturalSize?.w ?? 0;
                      const curH = aspect ? curW / aspect : 0;
                      const img = (
                        <img
                          id="dragablesModalImg"
                          src={ImageUrl}
                          alt={ImageUrl.split('/').pop()}
                          draggable={false}
                          onLoad={onModalImageLoad}
                          style={{
                            display: 'block',
                            margin: useDW ? 0 : '0 auto',
                            width: useDW ? '100%' : undefined,
                            maxWidth: useDW ? undefined : '100%',
                          }}
                        />
                      );
                      // Wait for natural size before enabling the resize handle.
                      if (!NaturalSize || !curW) {
                        return img;
                      }
                      return (
                        <Resizable
                          width={curW}
                          height={curH}
                          lockAspectRatio
                          minConstraints={[80, 80]}
                          resizeHandles={['se']}
                          onResizeStart={onModalImageResizeStart}
                          onResize={onModalImageResize}
                          onResizeStop={onModalImageResizeStop}
                        >
                          {/* q-image-resizable (styles.css) styles the default
                              react-resizable handle as a blue circle. */}
                          <div
                            className="q-image-resizable"
                            style={{
                              position: 'relative',
                              width: curW,
                              margin: useDW ? 0 : '0 auto',
                            }}
                          >
                            {img}
                          </div>
                        </Resizable>
                      );
                    })()}
                  </DragContainerOuter>
                  <IconFieldActions className="flex">
                    <Button
                      disabled={showLoading}
                      variant="contained"
                      onClick={() => setShowDraggableModal(false)}
                    >
                      Close
                    </Button>

                    <Button
                      disabled={showLoading}
                      id="formSubmitBtn"
                      type="button"
                      variant="contained"
                      color="primary"
                      onClick={handleSubmit}
                    >
                      Insert in document
                    </Button>
                  </IconFieldActions>
                </PaperInnerDiv>
              </PaperDiv>
            </Modal>
          )}
        </div>
      </RootDiv>
    );
  }
);

function parseUrl(url: string) {
  const regex = url.includes('edit')
    ? /\/admin\/tenants\/(\d+)\/companies\/(\d+)\/screens\/create\/edit\/(\d+)/
    : /\/admin\/tenants\/(\d+)\/companies\/(\d+)\/screens\/create(\/(\d+))?/; // Optional ID in create mode

  const match = RegExp(regex).exec(url);
  if (match) {
    const tenantId = match[1];
    const companyId = match[2];
    const screenId = url.includes('edit') ? match[3] : match[4] || ''; // screenId only for edit mode or optional in create
    return { tenantId, companyId, screenId };
  } else {
    return null;
  }
}
